import { singletonPeerRevisions } from "./singleton-peers";
import {
  createConnectionV4,
  deleteConnectionV4,
  isRejectedConnectionOperation,
  replaceConnectionV4,
} from "@/lib/data/requests/connections/connection";
import type {
  Connection,
  ConnectionCreate,
  ConnectionContactPreview,
  SourceOwnedConnectionCreate,
} from "@/lib/types";
import { formatContactsName } from "@/lib/utils/format";

type SourceWithConnections = { connections: Connection[]; id: string };

export const getActiveContactConnections = (
  source: SourceWithConnections | undefined,
  sourceObjectType: string,
  connectionType: string,
) => {
  const sourceId = source?.id;
  return (source?.connections ?? []).filter(
    (connection) =>
      connection.sourceObjectType === sourceObjectType &&
      connection.sourceObjectId === sourceId &&
      connection.targetObjectType === "contact" &&
      connection.connectionType === connectionType &&
      (connection.state === "active" || connection.state === "scheduled"),
  );
};

export const createContactConnectionInput = (
  sourceObjectId: string,
  sourceObjectType: string,
  targetObjectId: string,
  connectionType: string,
): ConnectionCreate => ({
  connectionType,
  operationId: crypto.randomUUID(),
  sourceObjectId,
  sourceObjectType,
  targetObjectId,
  targetObjectType: "contact",
});

export const createContactConnectionChildInput = (
  targetObjectId: string,
  connectionType: string,
): SourceOwnedConnectionCreate => ({
  connectionType,
  targetObjectId,
  targetObjectType: "contact",
});

export const getConnectionContactName = (
  contact: Connection["contact"] | undefined,
) => (contact ? formatContactsName(contact as never) : undefined);

export const getConnectionFormContact = (connection?: Connection) => {
  if (!connection?.contact) return undefined;
  return {
    contactName:
      getConnectionContactName(connection.contact) ?? "Unknown contact",
    id: connection.contact.id,
    type: connection.contact.type,
  };
};

export const getConnectionPreviewContact = (
  connection?: Connection,
): ConnectionContactPreview | undefined => {
  const contact = connection?.contact;
  if (!contact) return undefined;
  return {
    ...contact,
    addresses: contact.addresses ?? [],
    faxes: contact.faxes ?? [],
    contactName: getConnectionContactName(contact) ?? "Unknown contact",
    emails:
      contact.emails ??
      (contact.primaryEmail
        ? [
            {
              ...contact.primaryEmail,
              contactId: contact.id,
              dateCreated: "",
              dateModified: "",
              description: null,
              firmId: "",
              isPrimary: true,
            },
          ]
        : []),
    phones:
      contact.phones ??
      (contact.primaryPhone
        ? [
            {
              ...contact.primaryPhone,
              contactId: contact.id,
              dateCreated: "",
              dateModified: "",
              description: null,
              firmId: "",
              isPrimary: true,
            },
          ]
        : []),
  };
};

const pendingSlotEdits = new Map<
  string,
  { desiredContactId?: string; command: () => Promise<unknown> }
>();

export const syncSingleContactConnection = async ({
  connectionType,
  current,
  currentConnections,
  desiredContactId,
  sourceObjectId,
  sourceObjectType,
  matterId,
}: {
  connectionType: string;
  current?: Connection;
  currentConnections?: Connection[];
  desiredContactId?: string;
  sourceObjectId: string;
  sourceObjectType: string;
  matterId?: string;
}) => {
  // Freeze this slot's explicit assignment and peer intent until acknowledged.
  // An owner refresh after a lost acknowledgement can reorder the visible slot.
  const key = JSON.stringify([
    sourceObjectType,
    sourceObjectId,
    connectionType,
  ]);
  const pending = pendingSlotEdits.get(key);
  let command =
    pending?.desiredContactId === desiredContactId
      ? pending?.command
      : undefined;
  if (!command) pendingSlotEdits.delete(key);
  if (!command) {
    if (current?.targetObjectId === desiredContactId) return;
    const selected = current ? { ...current, matterId } : undefined;
    const peers = selected
      ? singletonPeerRevisions(selected, currentConnections ?? [])
      : {};
    if (selected && desiredContactId) {
      const replacement = {
        dateModified: selected.dateModified,
        operation: "replace" as const,
        peerRevisions: peers,
        replacement: createContactConnectionInput(
          sourceObjectId,
          sourceObjectType,
          desiredContactId,
          connectionType,
        ),
      };
      command = () => replaceConnectionV4(selected.id, replacement, selected);
    } else if (selected) {
      const operationId = crypto.randomUUID();
      command = () =>
        deleteConnectionV4(selected, matterId, operationId, peers);
    } else if (desiredContactId) {
      const input = {
        ...createContactConnectionInput(
          sourceObjectId,
          sourceObjectType,
          desiredContactId,
          connectionType,
        ),
        matterId,
      };
      command = () => createConnectionV4(input);
    } else return;
    pendingSlotEdits.set(key, { desiredContactId, command });
  }
  try {
    await command();
    pendingSlotEdits.delete(key);
  } catch (error) {
    if (isRejectedConnectionOperation(error)) pendingSlotEdits.delete(key);
    throw error;
  }
};
